/**
 * IcalFeedsModal — gere os feeds OTA persistentes (.ics) de UM apartamento.
 * O cron de 6h em 6h sincroniza automaticamente todos os feeds enabled,
 * mas o admin pode também forçar um sync manual aqui.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Plus, Trash2, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Power,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";

const SOURCE_OPTIONS = [
  { id: "booking", label: "Booking.com" },
  { id: "airbnb",  label: "Airbnb" },
  { id: "vrbo",    label: "Vrbo / HomeAway" },
  { id: "other",   label: "Outra (.ics)" },
];

const TIME_SLOTS = ["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"];

export default function IcalFeedsModal({ open, apartmentId, apartmentName, onClose, onChange }) {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // feed id currently being sync'd
  // New-feed form
  const [draft, setDraft] = useState({
    source: "booking", ical_url: "", default_cleaning_time: "11:00",
    same_day_finish_by: "14:00",
  });
  const [saving, setSaving] = useState(false);

  async function reload() {
    if (!apartmentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/apartments/${apartmentId}/ical-feeds`);
      setFeeds(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (!open) return;
    setDraft({ source: "booking", ical_url: "", default_cleaning_time: "11:00", same_day_finish_by: "14:00" });
    reload();
    // eslint-disable-next-line
  }, [open, apartmentId]);

  async function addFeed() {
    if (!draft.ical_url) return toast.error("Cole o URL do calendário .ics.");
    setSaving(true);
    try {
      await api.post(`/admin/apartments/${apartmentId}/ical-feeds`, {
        source: draft.source,
        ical_url: draft.ical_url.trim(),
        default_cleaning_time: draft.default_cleaning_time,
        same_day_finish_by: draft.same_day_finish_by,
        enabled: true,
      });
      toast.success("Feed adicionado. Próxima sincronização em ≤1 h.");
      setDraft({ source: "booking", ical_url: "", default_cleaning_time: "11:00", same_day_finish_by: "14:00" });
      reload();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  async function toggle(feed) {
    try {
      await api.patch(`/admin/ical-feeds/${feed.id}`, { enabled: !feed.enabled });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function setDefaultTime(feed, time) {
    try {
      await api.patch(`/admin/ical-feeds/${feed.id}`, { default_cleaning_time: time });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function syncNow(feed) {
    setBusy(feed.id);
    try {
      const { data } = await api.post(`/admin/ical-feeds/${feed.id}/sync`, {});
      const r = data.result || {};
      const msg = `${r.ok_count || 0} criadas`
                + (r.skipped_count ? `, ${r.skipped_count} já existiam` : "")
                + (r.fail_count ? `, ${r.fail_count} com erro` : "")
                + (r.error ? ` · ${r.error}` : "");
      r.fail_count || r.error ? toast.warning(msg) : toast.success(msg);
      reload();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  }

  async function remove(feed) {
    if (!confirm("Remover este feed? As limpezas já criadas mantêm-se.")) return;
    try {
      await api.delete(`/admin/ical-feeds/${feed.id}`);
      reload();
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const sourceLabel = useMemo(
    () => Object.fromEntries(SOURCE_OPTIONS.map(s => [s.id, s.label])),
    [],
  );

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
         data-testid="ical-feeds-modal">
      <div className="lux-card w-full md:max-w-3xl max-h-[95vh] flex flex-col bg-[#041f1b] border-[#d4af37]/30">
        <div className="px-6 py-4 border-b border-[#1a4640] flex items-baseline justify-between gap-3">
          <div>
            <div className="eyebrow text-[#d4af37]">Sincronização automática OTA</div>
            <h2 className="font-display text-2xl mt-1">{apartmentName || "Apartamento"}</h2>
            <p className="text-xs text-[#a0b2af] mt-1">
              Os feeds aqui guardados são sincronizados automaticamente <strong>de hora em hora</strong>.
              Re-sincronizar é seguro — datas duplicadas são ignoradas.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-white"
                  data-testid="ical-feeds-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Existing feeds */}
          <section>
            <div className="eyebrow text-[#a0b2af] mb-2 flex items-center gap-2">
              Feeds ativos ({feeds.length})
              {loading && <Loader2 size={12} className="animate-spin" />}
            </div>
            {feeds.length === 0 ? (
              <div className="lux-card p-4 text-xs text-[#a0b2af]" data-testid="ical-feeds-empty">
                Sem feeds. Adicione um abaixo para começar a sincronização automática.
              </div>
            ) : (
              <div className="space-y-2">
                {feeds.map(f => {
                  const r = f.last_sync_result;
                  const tone = !r ? "border-[#1a4640]"
                              : r.error ? "border-[#ef4444]/40 bg-[#ef4444]/5"
                              : (r.ok_count > 0 || r.skipped_count >= 0) ? "border-[#1a4640]" : "border-[#1a4640]";
                  const lastTxt = f.last_sync_at
                    ? new Date(f.last_sync_at).toLocaleString("pt-PT")
                    : "ainda não sincronizado";
                  return (
                    <div key={f.id}
                         className={`lux-card p-3 ${tone} ${!f.enabled ? "opacity-60" : ""}`}
                         data-testid={`ical-feed-row-${f.id}`}>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">
                            <span className="text-[#d4af37] font-semibold">{sourceLabel[f.source] || f.source}</span>
                            <span className="text-[10px] uppercase tracking-widest text-[#a0b2af] ml-2">
                              · hora padrão {f.default_cleaning_time}
                              {f.same_day_finish_by && (
                                <span className="ml-2 text-[#fca5a5]">
                                  · 🔥 turnover até {f.same_day_finish_by}
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="text-[11px] text-[#a0b2af] truncate font-mono mt-0.5">
                            {f.ical_url}
                          </div>
                          <div className="text-[11px] mt-1.5 flex items-center gap-1.5">
                            {r?.error ? (
                              <span className="text-[#fca5a5]"><AlertTriangle size={11} className="inline mr-1" />{r.error}</span>
                            ) : r ? (
                              <span className="text-[#10b981]">
                                <CheckCircle2 size={11} className="inline mr-1" />
                                {r.ok_count} criadas · {r.skipped_count} já existiam{r.fail_count ? ` · ${r.fail_count} com erro` : ""}
                              </span>
                            ) : (
                              <span className="text-[#a0b2af]">aguarda primeira sincronização</span>
                            )}
                            <span className="text-[#a0b2af] ml-2">· {lastTxt}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <select value={f.default_cleaning_time}
                                  onChange={e => setDefaultTime(f, e.target.value)}
                                  className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1 text-xs outline-none"
                                  data-testid={`ical-feed-time-${f.id}`}>
                            {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <button onClick={() => syncNow(f)} disabled={busy === f.id}
                                  className="p-1.5 text-[#d4af37] hover:bg-[#d4af37]/10 rounded-sm disabled:opacity-50"
                                  title="Sincronizar agora"
                                  data-testid={`ical-feed-sync-${f.id}`}>
                            {busy === f.id
                              ? <Loader2 size={14} className="animate-spin" />
                              : <RefreshCw size={14} />}
                          </button>
                          <button onClick={() => toggle(f)}
                                  className={`p-1.5 rounded-sm ${f.enabled ? "text-[#10b981] hover:bg-[#10b981]/10" : "text-[#a0b2af] hover:bg-white/5"}`}
                                  title={f.enabled ? "Desativar" : "Ativar"}
                                  data-testid={`ical-feed-toggle-${f.id}`}>
                            <Power size={14} />
                          </button>
                          <button onClick={() => remove(f)}
                                  className="p-1.5 text-[#fca5a5] hover:bg-[#ef4444]/10 rounded-sm"
                                  title="Remover"
                                  data-testid={`ical-feed-delete-${f.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Add new feed */}
          <section className="border-t border-[#1a4640] pt-5">
            <div className="eyebrow text-[#d4af37] mb-2">Adicionar novo feed</div>
            <div className="grid md:grid-cols-4 gap-3">
              <select value={draft.source}
                      onChange={e => setDraft(d => ({ ...d, source: e.target.value }))}
                      className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none"
                      data-testid="ical-feed-new-source">
                {SOURCE_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <select value={draft.default_cleaning_time}
                      onChange={e => setDraft(d => ({ ...d, default_cleaning_time: e.target.value }))}
                      className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none"
                      data-testid="ical-feed-new-time">
                {TIME_SLOTS.map(t => <option key={t} value={t}>Hora padrão {t}</option>)}
              </select>
              <select value={draft.same_day_finish_by}
                      onChange={e => setDraft(d => ({ ...d, same_day_finish_by: e.target.value }))}
                      className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none"
                      title="Hora-limite de turnover quando há check-in no mesmo dia"
                      data-testid="ical-feed-new-finishby">
                {TIME_SLOTS.map(t => <option key={t} value={t}>Turnover até {t}</option>)}
              </select>
              <button onClick={addFeed} disabled={saving || !draft.ical_url}
                      className="btn-gold px-4 py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="ical-feed-add-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {saving ? "A validar…" : "Adicionar"}
              </button>
            </div>
            <input type="url"
                   value={draft.ical_url}
                   onChange={e => setDraft(d => ({ ...d, ical_url: e.target.value }))}
                   placeholder="https://… .ics"
                   className="w-full mt-3 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none font-mono"
                   data-testid="ical-feed-new-url" />
            <p className="text-[11px] text-[#a0b2af] mt-1">
              Validamos o URL ao adicionar. Se a plataforma exigir autenticação, gere um link público de exportação.
            </p>
          </section>
        </div>

        <div className="px-6 py-3 border-t border-[#1a4640] flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0b2af] hover:text-white"
                  data-testid="ical-feeds-done">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
